import { useFilters } from '@statsbomb/kitbag-global-football';
import { applyEventFilterGroups } from '@statsbomb/kitbag-global-football/state';
import { useAtomValue, useSetAtom } from 'jotai';
import { useEffect } from 'react';
import { useMatch } from 'react-router-dom';
import { minuteMinMaxFilterAtom, xgMinMaxFilterAtom } from '@/atoms/filters/events';
import { selectedPositionsFilterParamsAtom } from '@/atoms/filters/highLevel/positions';
import { selectedPitchFilterCoordsAtom } from '@/atoms/filters/pitchFilter';
import { unwrappedTeamSelectedGamesForQueryAtom } from '@/atoms/filters/team/teamGames';
import { appliedMappingMetricAtom, mappedMetricReadyDebounceObject } from '@/atoms/metric/metricFilterMapping';
import { obvSelectedAtom } from '@/atoms/queries/eventData';
import { playerAggsFilterParamsDebounceObject as teamSquadAggsFilterParamsDebounceObject } from '@/atoms/queries/player/playerAggData';
import { teamSquadFilterParamsAtom } from '@/atoms/queries/team/teamAggData';
import { teamEventMetricFiltersDebounceObject } from '@/atoms/queries/team/teamEventData';
import { teamRadarFilterDebounceObject } from '@/atoms/queries/team/teamRadar';
import { teamIdAtom } from '@/atoms/team/team';
import { DEFAULT_DEBOUNCE_MS } from '@/consts/api';
import { PLAY_PATTERN_API_FIELD } from '@/consts/playPatterns';
import { useEventsPerspectiveFeature } from '@/hooks/featureFlags';
import { useDeepEffect } from '@/hooks/useDeepEffect';
import { optionsToValueStrings, toObject } from '@/utils/object';

export const useSetTeamDataDebouncedFilterParams = () => {
  const [
    {
      eventNames: selectedEventNames,
      outcomes: selectedOutcomes,
      playPatterns: selectedPlayPatterns,
      eventFilterGroups: selectedEventFilterGroups,
      matchPeriods: selectedMatchPeriods,
      eventsPerspective: selectedEventsPerspective,
    },
  ] = useFilters('main');
  const isEventsPerspectiveEnabled = useEventsPerspectiveFeature();
  const teamId = useAtomValue(teamIdAtom);
  const teamGameIds = optionsToValueStrings(useAtomValue(unwrappedTeamSelectedGamesForQueryAtom));
  const teamSquadFilterParams = useAtomValue(teamSquadFilterParamsAtom);
  const selectedPitchFilterCoords = useAtomValue(selectedPitchFilterCoordsAtom);
  const obvSelected = useAtomValue(obvSelectedAtom);
  const selectedXg = useAtomValue(xgMinMaxFilterAtom);
  const selectedMinute = useAtomValue(minuteMinMaxFilterAtom);
  const selectedPositions = useAtomValue(selectedPositionsFilterParamsAtom);
  const { debouncedValueAtom: teamEventMetricFiltersAtom, timeoutDelayAtom: teamEventMetricFiltersTimeoutDelayAtom } =
    teamEventMetricFiltersDebounceObject;
  const setTeamEventMetricFilters = useSetAtom(teamEventMetricFiltersAtom);
  const setTeamEventMetricFiltersTimeoutDelay = useSetAtom(teamEventMetricFiltersTimeoutDelayAtom);

  const { debouncedValueAtom: teamRadarFilterAtom, timeoutDelayAtom: teamRadarFilterTimeDelayAtom } =
    teamRadarFilterDebounceObject;
  const setTeamRadarFilter = useSetAtom(teamRadarFilterAtom);
  const setTeamRadarFilterTimeDelay = useSetAtom(teamRadarFilterTimeDelayAtom);

  const {
    debouncedValueAtom: teamSquadAggsFilterParamsDebounceAtom,
    timeoutDelayAtom: teamSquadAggsFilterParamsTimeoutDelayAtom,
  } = teamSquadAggsFilterParamsDebounceObject;
  const setTeamSquadAggsFilterParamsDebounce = useSetAtom(teamSquadAggsFilterParamsDebounceAtom);
  const setTeamSquadAggsFilterParamsTimeoutDelay = useSetAtom(teamSquadAggsFilterParamsTimeoutDelayAtom);

  const appliedMappingMetric = useAtomValue(appliedMappingMetricAtom);
  const { debouncedValueAtom: mappedMetricReadyAtom, timeoutDelayAtom: mappedMetricReadyTimeoutDelayAtom } =
    mappedMetricReadyDebounceObject;
  const setMappedMetricReady = useSetAtom(mappedMetricReadyAtom);
  const setMappedMetricReadyTimeoutDelay = useSetAtom(mappedMetricReadyTimeoutDelayAtom);

  const updateTimeouts = (timeout: number) => {
    setMappedMetricReadyTimeoutDelay(timeout);
    setTeamEventMetricFiltersTimeoutDelay(timeout);
    setTeamRadarFilterTimeDelay(timeout);
    setTeamSquadAggsFilterParamsTimeoutDelay(timeout);
  };

  // we want the event filters to apply only when on the team vis page/tab
  const isTeamVisPage = !!useMatch('/:locale/team/:teamId/vis/*');
  const visOnlyEventFilters = isTeamVisPage
    ? {
        detail: {
          ...toObject('event.type', selectedEventNames),
          ...toObject('event.attributes.outcome', selectedOutcomes),
          ...toObject(PLAY_PATTERN_API_FIELD, selectedPlayPatterns),
          ...toObject('event.attributes.player_position', selectedPositions),
          ...toObject('event.period', selectedMatchPeriods),
        },
        groups: selectedEventFilterGroups,
        pitch: selectedPitchFilterCoords,
        gte: { ...obvSelected.min, ...selectedXg.min, ...selectedMinute.min },
        lte: { ...obvSelected.max, ...selectedXg.max, ...selectedMinute.max },
      }
    : { detail: {}, groups: [], pitch: {}, gte: {}, lte: {} };
  // For is the default, so it adds no param and the For requests stay as they were
  const isAgainstPerspective = isTeamVisPage && isEventsPerspectiveEnabled && selectedEventsPerspective === 'against';

  useEffect(() => {
    // When team id updates, set the timeout delay to 0 so that our new query runs immediately.
    updateTimeouts(0);
  }, [teamId]);

  useDeepEffect(() => {
    // If there's no games, set the timeout to 0 as this won't trigger an API call.
    if (!teamGameIds.length) updateTimeouts(0);
    const { detail, groups, pitch, gte, lte } = visOnlyEventFilters;
    const baseTeamEventFilter = {
      in: {
        ...detail,
        'event.game_id': teamGameIds,
      },
      inside: pitch,
      gte,
      lte,
    };
    setTeamEventMetricFilters({
      ...applyEventFilterGroups(baseTeamEventFilter, groups),
      ...(isAgainstPerspective && { perspective: 'against' }),
    });
    setMappedMetricReady(appliedMappingMetric);
    setTeamRadarFilter({
      in: { game_id: teamGameIds },
    });
    setTeamSquadAggsFilterParamsDebounce(teamSquadFilterParams);
    // If there were games this time, set the timeout so that we debounce subsequent calls
    if (teamGameIds.length) updateTimeouts(DEFAULT_DEBOUNCE_MS);
  }, [teamGameIds, visOnlyEventFilters, isAgainstPerspective, teamSquadFilterParams, appliedMappingMetric]);
};
